import { useState } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ScrollArea } from './ui/scroll-area';
import { Sparkles, Send, Copy, ImageIcon, FileText, X, Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const AIAssistant = ({ isOpen, onClose, onInsert, token }) => {
  const [mode, setMode] = useState('text'); // text, image, chat
  const [prompt, setPrompt] = useState('');
  const [context, setContext] = useState('');
  const [contentType, setContentType] = useState('description');
  const [imageStyle, setImageStyle] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const generateText = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-text`, {
        prompt,
        context,
        content_type: contentType
      }, { headers });
      
      setResult({ type: 'text', content: res.data.content });
      toast.success('Content generated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate text');
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the image you want');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/ai/generate-image`, {
        prompt,
        style: imageStyle
      }, { headers });
      
      setResult({ type: 'image', content: res.data.image_data });
      toast.success('Image generated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const sendChat = async () => {
    if (!prompt.trim()) return;
    
    const userMessage = prompt;
    setChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setPrompt('');
    setLoading(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/ai/chat`, {
        message: userMessage
      }, { headers });
      
      setChatHistory(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Chat failed');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (result?.content) {
      navigator.clipboard.writeText(result.content);
      toast.success('Copied to clipboard!');
    }
  };

  const insertContent = () => {
    if (result?.content && onInsert) {
      onInsert(result.content, result.type);
      toast.success('Content inserted!');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-background border-l shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b bg-muted">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <h2 className="font-semibold">AI Assistant</h2>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Mode Selector */}
      <div className="flex p-2 gap-2 border-b">
        <Button
          variant={mode === 'text' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setMode('text')}
          className="flex-1"
        >
          <FileText className="w-4 h-4 mr-1" />
          Text
        </Button>
        <Button
          variant={mode === 'image' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setMode('image')}
          className="flex-1"
        >
          <ImageIcon className="w-4 h-4 mr-1" />
          Image
        </Button>
        <Button
          variant={mode === 'chat' ? 'default' : 'ghost'}
          size="sm"
          onClick={() => setMode('chat')}
          className="flex-1"
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Chat
        </Button>
      </div>

      {/* Content Area */}
      <ScrollArea className="flex-1 p-4">
        {mode === 'text' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Content Type</label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="description">Course/Lesson Description</SelectItem>
                  <SelectItem value="outline">Course Outline</SelectItem>
                  <SelectItem value="quiz">Quiz Questions</SelectItem>
                  <SelectItem value="announcement">Announcement</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Topic/Subject *</label>
              <Input
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g., Introduction to Woodworking"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Additional Context (optional)</label>
              <Textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder="Any specific points to include..."
                rows={3}
              />
            </div>
            
            <Button onClick={generateText} disabled={loading} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate Text
            </Button>
          </div>
        )}

        {mode === 'image' && (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1 block">Style</label>
              <Select value={imageStyle} onValueChange={setImageStyle}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="artistic">Artistic</SelectItem>
                  <SelectItem value="minimalist">Minimalist</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-1 block">Image Description *</label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the image you want... e.g., 'Modern kitchen renovation with oak cabinets'"
                rows={4}
              />
            </div>
            
            <Button onClick={generateImage} disabled={loading} className="w-full">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
              Generate Image
            </Button>
          </div>
        )}

        {mode === 'chat' && (
          <div className="space-y-4">
            <div className="space-y-3 min-h-[200px]">
              {chatHistory.length === 0 && (
                <p className="text-muted-foreground text-sm text-center py-8">
                  Ask me anything about creating course content, writing descriptions, or generating ideas!
                </p>
              )}
              {chatHistory.map((msg, i) => (
                <div
                  key={i}
                  className={`p-3 rounded-lg ${
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground ml-8'
                      : 'bg-muted mr-8'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              ))}
              {loading && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result Display */}
        {result && mode !== 'chat' && (
          <Card className="mt-4">
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Generated {result.type === 'text' ? 'Content' : 'Image'}
                <div className="flex gap-1">
                  {result.type === 'text' && (
                    <Button variant="ghost" size="icon" onClick={copyToClipboard}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  )}
                  {onInsert && (
                    <Button variant="ghost" size="sm" onClick={insertContent}>
                      Insert
                    </Button>
                  )}
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="py-0 pb-3">
              {result.type === 'text' ? (
                <div className="prose prose-sm max-w-none max-h-64 overflow-y-auto p-2 bg-muted rounded text-sm">
                  <pre className="whitespace-pre-wrap font-sans">{result.content}</pre>
                </div>
              ) : (
                <img
                  src={result.content}
                  alt="Generated"
                  className="w-full rounded-lg"
                />
              )}
            </CardContent>
          </Card>
        )}
      </ScrollArea>

      {/* Chat Input (only in chat mode) */}
      {mode === 'chat' && (
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Input
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Ask anything..."
              onKeyPress={(e) => e.key === 'Enter' && sendChat()}
            />
            <Button onClick={sendChat} disabled={loading} size="icon">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistant;
